import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  User,
  Moon,
  Save,
  CreditCard,
  Plus,
  Star,
  Trash2,
  X,
  Check,
  Palette,
  Lock,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { ThemeContext, ACCENTS } from '../context/ThemeContext'; // Import ThemeContext
import {
  getClientPaymentMethods,
  createPaymentMethodSetupIntent,
  setDefaultPaymentMethod,
  removePaymentMethod,
} from '../functions/portalBillingApi';
import { getMyProfile, updateMyProfile } from '../functions/portalProfileApi';
import { getPasswordStatus, setPassword } from '../functions/auth_fethcers';
import { CardEntry } from './CardEntry';
import { SectionCard, StatusBadge, SolidButton, inputClass, labelClass, PageHeader, Spinner } from './portal/PortalUI';

const Section = ({ title, children, icon: Icon }) => (
  <SectionCard title={title} icon={Icon}>
    {children}
  </SectionCard>
);

const ToggleSwitch = ({ enabled, onChange, label }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-grayteck-700 dark:text-grayteck-300">{label}</span>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-grayteck-600 dark:focus:ring-gold-400 focus:ring-offset-2 dark:focus:ring-offset-darkbg-800 ${
        enabled ? 'bg-grayteck-700 dark:bg-gold-400' : 'bg-grayteck-300 dark:bg-darkbg-500'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const AccentPicker = ({ value, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <span className="text-sm font-medium text-grayteck-800 dark:text-grayteck-200">Accent color</span>
      <p className="text-xs text-grayteck-500 dark:text-grayteck-400 mt-0.5">
        Sets the highlight color across your portal
      </p>
    </div>
    <div className="flex items-center gap-2.5">
      {ACCENTS.map((a) => {
        const selected = value === a.id;
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => onChange(a.id)}
            aria-label={a.name}
            aria-pressed={selected}
            title={a.name}
            className={`relative w-8 h-8 rounded-full transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-darkbg-800 focus-visible:ring-grayteck-500 ${
              selected
                ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-darkbg-800'
                : 'ring-1 ring-black/10 dark:ring-white/15'
            }`}
            style={{
              backgroundColor: a.swatch,
              ...(selected ? { '--tw-ring-color': a.swatch } : {}),
            }}
          >
            {selected && (
              <Check
                className="absolute inset-0 m-auto w-4 h-4"
                style={{ color: a.id === 'gold' ? '#243b53' : '#ffffff' }}
                strokeWidth={3}
              />
            )}
          </button>
        );
      })}
    </div>
  </div>
);

const CardRow = ({ pm, isDefault, onSetDefault, onRemove, isMutating }) => (
  <div className="flex items-center justify-between py-3.5 border-b border-grayteck-200 dark:border-darkbg-600 last:border-b-0">
    <div className="flex items-center">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-grayteck-100 dark:bg-darkbg-700 mr-3">
        <CreditCard className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
      </span>
      <div>
        <p className="text-sm font-semibold text-grayteck-900 dark:text-grayteck-100">
          {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} •••• {pm.last4}
        </p>
        <p className="text-xs text-grayteck-600 dark:text-grayteck-400">
          Expires {String(pm.exp_month).padStart(2, '0')}/{pm.exp_year}
        </p>
      </div>
      {isDefault && (
        <StatusBadge
          tone="attention"
          label={
            <>
              <Star className="w-3 h-3 mr-1" /> Default
            </>
          }
          className="ml-3"
        />
      )}
    </div>
    <div className="flex items-center space-x-3">
      {!isDefault && (
        <button
          onClick={() => onSetDefault(pm.pm_id)}
          disabled={isMutating}
          className="text-sm font-semibold text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-100 disabled:opacity-50 transition-colors"
        >
          Set default
        </button>
      )}
      <button
        onClick={() => onRemove(pm.pm_id)}
        disabled={isMutating}
        className="p-1.5 rounded-md text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
        aria-label="Remove card"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const ClientPortalSettings = () => {
  const { darkMode, toggleDarkMode, accent, setAccent } = useContext(ThemeContext); // Access darkMode, toggleDarkMode and accent

  const queryClient = useQueryClient();

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useQuery('myProfile', getMyProfile, {
    staleTime: 60000,
    retry: 1,
  });

  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    if (profile) {
      setProfileForm({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
      });
    }
  }, [profile]);

  const updateProfileMutation = useMutation(updateMyProfile, {
    onSuccess: (updated) => {
      queryClient.setQueryData('myProfile', updated);
      toast.success('Profile updated');
      setProfileError(null);
    },
    onError: (error) => {
      setProfileError(error.message || 'Failed to update profile');
    },
  });

  const handleProfileFieldChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = () => {
    const name = profileForm.name.trim();
    const email = profileForm.email.trim();
    const phone = profileForm.phone.trim();

    if (!name) {
      setProfileError('Name cannot be empty');
      return;
    }
    if (!phone) {
      setProfileError('Phone number cannot be empty');
      return;
    }

    setProfileError(null);
    updateProfileMutation.mutate({ name, email, phone });
  };

  // --- Password (sign-in security) ---
  const { data: passwordStatus, isLoading: isLoadingPasswordStatus } = useQuery(
    'passwordStatus',
    getPasswordStatus,
    { staleTime: 60000, retry: 1 }
  );
  const hasPassword = Boolean(passwordStatus?.has_password);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState(null);

  const setPasswordMutation = useMutation(
    ({ newPassword, currentPassword }) => setPassword(newPassword, currentPassword),
    {
      onSuccess: () => {
        toast.success(hasPassword ? 'Password changed' : 'Password set — you can now sign in with it');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError(null);
        queryClient.invalidateQueries('passwordStatus');
      },
      onError: (error) => {
        setPasswordError(error.detail || error.message || 'Failed to update password');
      },
    }
  );

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSavePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (hasPassword && !currentPassword) {
      setPasswordError('Enter your current password to change it');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordError(null);
    setPasswordMutation.mutate({
      newPassword,
      currentPassword: hasPassword ? currentPassword : null,
    });
  };

  const [showAddCard, setShowAddCard] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);
  const [isStartingSetup, setIsStartingSetup] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  // Lazy-init, matching PublicDocViewer.js: only load Stripe.js when this
  // component actually mounts, not at module scope (module scope would fire
  // on every route since App.js statically imports this file).
  const [stripePromise] = useState(() =>
    loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  );

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    isError: isErrorPaymentMethods,
    refetch: refetchPaymentMethods,
  } = useQuery('clientPaymentMethods', getClientPaymentMethods, {
    // Keep saved cards fresh without manual refresh (updates land via webhooks).
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    retry: 1,
  });

  const paymentMethods = paymentMethodsData?.paymentMethods || [];
  const defaultPaymentMethodId = paymentMethodsData?.defaultPaymentMethodId || null;

  const setDefaultMutation = useMutation(setDefaultPaymentMethod, {
    onSuccess: () => {
      toast.success('Default card updated');
      queryClient.invalidateQueries('clientPaymentMethods');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to set default card');
    },
  });

  const removeMutation = useMutation(removePaymentMethod, {
    onSuccess: (data) => {
      toast.success('Card removed');
      queryClient.setQueryData('clientPaymentMethods', (old) => ({
        defaultPaymentMethodId: data?.default_payment_method_id ?? old?.defaultPaymentMethodId ?? null,
        paymentMethods: (old?.paymentMethods || []).filter((pm) => pm.pm_id !== data?.removed),
      }));
      queryClient.invalidateQueries('clientPaymentMethods');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove card');
    },
  });

  const isMutatingCards = setDefaultMutation.isLoading || removeMutation.isLoading;

  const handleStartAddCard = async () => {
    setIsStartingSetup(true);
    try {
      const { setupIntentSecret: secret } = await createPaymentMethodSetupIntent();
      setSetupIntentSecret(secret);
      setShowAddCard(true);
    } catch (error) {
      toast.error(error.message || 'Failed to start card setup');
    } finally {
      setIsStartingSetup(false);
    }
  };

  const handleCancelAddCard = () => {
    setShowAddCard(false);
    setSetupIntentSecret(null);
  };

  const handleCardEntrySuccess = async () => {
    setIsSavingCard(true);
    toast.success('Card saved. Syncing with your account...');
    setShowAddCard(false);
    setSetupIntentSecret(null);
    // The webhook (setup_intent.succeeded) persists PM display metadata
    // asynchronously; refetch now and once more shortly after so the new
    // card shows up without requiring a manual page reload.
    await refetchPaymentMethods();
    window.setTimeout(() => {
      refetchPaymentMethods();
    }, 2000);
    setIsSavingCard(false);
  };

  const handleCardEntryError = (error) => {
    toast.error(error?.message || 'Failed to save card');
  };

  return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto"
      >
        <PageHeader
          title="Settings"
          subtitle="Manage your profile, payment methods and preferences"
        />
        <Section title="Profile Information" icon={User}>
          {isLoadingProfile ? (
            <div className="flex justify-center py-6">
              <Spinner label="" />
            </div>
          ) : isErrorProfile ? (
            <p className="text-red-600 dark:text-red-400 text-sm py-2">
              Error loading profile. Please try again later.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => handleProfileFieldChange('name', e.target.value)}
                    className={inputClass}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => handleProfileFieldChange('email', e.target.value)}
                    className={inputClass}
                    placeholder="Your email"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => handleProfileFieldChange('phone', e.target.value)}
                    className={inputClass}
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              {profileError && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-3">{profileError}</p>
              )}

              <div className="mt-5 flex justify-end">
                <SolidButton
                  onClick={handleSaveProfile}
                  disabled={updateProfileMutation.isLoading}
                >
                  <Save className="w-4 h-4" />
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Profile'}
                </SolidButton>
              </div>
            </>
          )}
        </Section>

        <Section title="Sign-In & Security" icon={Lock}>
          {isLoadingPasswordStatus ? (
            <div className="flex justify-center py-6">
              <Spinner label="" />
            </div>
          ) : (
            <>
              <p className="text-sm text-grayteck-700 dark:text-grayteck-400 mb-4">
                {hasPassword
                  ? 'You have a password set. You can sign in with it or with a one-time emailed code.'
                  : 'You sign in with one-time emailed codes. Set a password to also sign in with email + password.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {hasPassword && (
                  <div>
                    <label className={labelClass}>Current Password</label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
                      className={inputClass}
                      placeholder="Your current password"
                    />
                  </div>
                )}
                <div>
                  <label className={labelClass}>New Password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.newPassword}
                    onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                    className={inputClass}
                    placeholder="At least 8 characters"
                  />
                </div>
                <div>
                  <label className={labelClass}>Confirm New Password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => handlePasswordFieldChange('confirmPassword', e.target.value)}
                    className={inputClass}
                    placeholder="Re-enter new password"
                  />
                </div>
              </div>

              {passwordError && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-3">{passwordError}</p>
              )}

              <div className="mt-5 flex justify-end">
                <SolidButton
                  onClick={handleSavePassword}
                  disabled={setPasswordMutation.isLoading}
                >
                  <Lock className="w-4 h-4" />
                  {setPasswordMutation.isLoading
                    ? 'Saving...'
                    : hasPassword
                      ? 'Change Password'
                      : 'Set Password'}
                </SolidButton>
              </div>
            </>
          )}
        </Section>

        <Section title="Payment Methods" icon={CreditCard}>
          {isLoadingPaymentMethods ? (
            <div className="flex justify-center py-6">
              <Spinner label="" />
            </div>
          ) : isErrorPaymentMethods ? (
            <p className="text-red-600 dark:text-red-400 text-sm py-2">
              Error loading payment methods. Please try again later.
            </p>
          ) : (
            <>
              {paymentMethods.length > 0 ? (
                <div>
                  {paymentMethods.map((pm) => (
                    <CardRow
                      key={pm.pm_id}
                      pm={pm}
                      isDefault={pm.pm_id === defaultPaymentMethodId}
                      onSetDefault={(pmId) => setDefaultMutation.mutate(pmId)}
                      onRemove={(pmId) => removeMutation.mutate(pmId)}
                      isMutating={isMutatingCards}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-grayteck-700 dark:text-grayteck-400 py-2">
                  No saved cards yet.
                </p>
              )}

              {showAddCard && setupIntentSecret ? (
                <div className="mt-4 p-4 border border-grayteck-200 dark:border-darkbg-600 rounded-lg bg-grayteck-50 dark:bg-darkbg-700">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
                      Add a card
                    </p>
                    <button
                      onClick={handleCancelAddCard}
                      className="text-grayteck-500 hover:text-grayteck-700 dark:hover:text-grayteck-300"
                      aria-label="Cancel adding card"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Elements stripe={stripePromise}>
                    <CardEntry
                      mode="setup"
                      clientSecret={setupIntentSecret}
                      submitLabel="Save card"
                      disabled={isSavingCard}
                      onSuccess={handleCardEntrySuccess}
                      onError={handleCardEntryError}
                      onProcessingChange={setIsSavingCard}
                    />
                  </Elements>
                </div>
              ) : (
                <button
                  onClick={handleStartAddCard}
                  disabled={isStartingSetup}
                  className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold text-grayteck-700 dark:text-grayteck-200 border border-dashed border-grayteck-400 dark:border-darkbg-500 hover:border-grayteck-600 dark:hover:border-grayteck-500 hover:bg-grayteck-50 dark:hover:bg-darkbg-700 transition-colors disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {isStartingSetup ? 'Starting...' : 'Add a card'}
                </button>
              )}
            </>
          )}
        </Section>

        {/* Notification toggles are hidden until they actually persist and
            drive real notifications (L25). Re-add once wired to a backend
            preference and a delivery mechanism. */}

        <Section title="Preferences" icon={Palette}>
          <ToggleSwitch
          enabled={darkMode} // Use global dark mode state
          onChange={toggleDarkMode} // Use global toggle function
            label="Dark Mode"
          />
          <div className="border-t border-grayteck-200 dark:border-darkbg-600 mt-1 pt-1">
            <AccentPicker value={accent} onChange={setAccent} />
          </div>
        </Section>
      </motion.div>
  );
};

export default ClientPortalSettings;